import { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import {
  useGetRenewalBoardQuery,
  useUpdateBondWorkflowMutation,
} from 'redux/features/employeeBondApi';
import Icon from 'components/AppIcon';
import BondCard from './BondCard';

const COLUMN_CONFIG = {
  UPCOMING: {
    title: 'Upcoming',
    color: 'from-amber-500 to-orange-500',
    icon: 'Clock',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    textColor: 'text-amber-700',
  },
  IN_PROGRESS: {
    title: 'In Progress',
    color: 'from-blue-500 to-indigo-500',
    icon: 'RefreshCw',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    textColor: 'text-blue-700',
  },
  ON_HOLD: {
    title: 'On Hold',
    color: 'from-rose-500 to-pink-500',
    icon: 'PauseCircle',
    bg: 'bg-rose-50',
    border: 'border-rose-200',
    textColor: 'text-rose-700',
  },
  DONE: {
    title: 'Done',
    color: 'from-emerald-500 to-teal-500',
    icon: 'CheckCircle',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    textColor: 'text-emerald-700',
  },
};

const COLUMN_ORDER = ['UPCOMING', 'IN_PROGRESS', 'ON_HOLD', 'DONE'];

const VALID_TRANSITIONS = {
  UPCOMING: ['IN_PROGRESS'],
  IN_PROGRESS: ['ON_HOLD', 'DONE'],
  ON_HOLD: ['IN_PROGRESS'],
  DONE: [],
};

const applyFilters = (bond, filters) => {
  if (filters.search) {
    const q = filters.search.toLowerCase();
    const name = bond.user ? `${bond.user.first_name} ${bond.user.last_name}`.toLowerCase() : '';
    const empId = (bond.user?.employee_id || '').toLowerCase();
    if (!name.includes(q) && !empId.includes(q)) return false;
  }
  if (filters.departmentId && filters.departmentId !== 'all') {
    const deptId = String(bond.user?.department?.id || '');
    if (deptId !== String(filters.departmentId)) return false;
  }
  if (filters.status && filters.status !== 'all') {
    if (bond.status !== filters.status) return false;
  }
  return true;
};

const RenewalBoard = ({ filters, onOpenRenewModal, onCardClick }) => {
  const { data: boardData, isLoading, isError, error } = useGetRenewalBoardQuery();
  const [updateWorkflow] = useUpdateBondWorkflowMutation();
  const [localColumns, setLocalColumns] = useState(null);
  const [bondMap, setBondMap] = useState({});

  useEffect(() => {
    if (boardData) {
      const allBonds = {};
      const cols = {};
      for (const colId of COLUMN_ORDER) {
        let bonds = (boardData[colId] || []).filter((b) => applyFilters(b, filters));
        bonds = bonds.filter((b) => b.status === 'ACTIVE');
        cols[colId] = {
          id: colId,
          title: COLUMN_CONFIG[colId].title,
          bondIds: bonds.map((b) => String(b.id)),
        };
        for (const b of bonds) {
          allBonds[String(b.id)] = b;
        }
      }
      setLocalColumns(cols);
      setBondMap(allBonds);
    }
  }, [boardData, filters]);

  const handleDragEnd = async (result) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index)
      return;

    const sourceColId = source.droppableId;
    const destColId = destination.droppableId;

    const allowed = VALID_TRANSITIONS[sourceColId] || [];
    if (!allowed.includes(destColId)) return;

    const newCols = { ...localColumns };
    const sourceCol = { ...newCols[sourceColId] };
    const destCol = { ...newCols[destColId] };

    const sourceBondIds = Array.from(sourceCol.bondIds);
    sourceBondIds.splice(source.index, 1);
    sourceCol.bondIds = sourceBondIds;

    const destBondIds = Array.from(destCol.bondIds);
    destBondIds.splice(destination.index, 0, draggableId);
    destCol.bondIds = destBondIds;

    newCols[sourceColId] = sourceCol;
    newCols[destColId] = destCol;

    setLocalColumns(newCols);

    const bond = bondMap[draggableId];

    if (destColId === 'DONE') {
      if (onOpenRenewModal) {
        onOpenRenewModal(bond);
      }
      const revert = { ...localColumns };
      setLocalColumns(revert);
      return;
    }

    try {
      await updateWorkflow({ id: parseInt(draggableId, 10), workflow_status: destColId }).unwrap();
    } catch {
      setLocalColumns(localColumns);
    }
  };

  const getColumnBorderColor = (colId) => {
    const map = {
      UPCOMING: 'border-amber-200',
      IN_PROGRESS: 'border-blue-200',
      ON_HOLD: 'border-rose-200',
      DONE: 'border-emerald-200',
    };
    return map[colId] || 'border-gray-200';
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 h-full">
        {COLUMN_ORDER.map((colId) => (
          <div
            key={colId}
            className="flex flex-col bg-white rounded-2xl border border-gray-200 p-4 shadow-sm animate-pulse"
          >
            <div className="h-6 bg-gray-200 rounded w-24 mb-4 flex-shrink-0" />
            <div className="flex-1 space-y-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-28 bg-gray-100 rounded-xl" />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
        <Icon name="AlertCircle" size={32} className="text-red-400 mx-auto mb-2" />
        <p className="text-red-600 font-medium">Failed to load renewal board</p>
        <p className="text-red-500 text-sm mt-1">
          {error?.data?.message || 'Please try refreshing'}
        </p>
      </div>
    );
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 h-full">
        {COLUMN_ORDER.map((colId) => {
          const col = localColumns?.[colId];
          const bonds = (col?.bondIds || []).map((id) => bondMap[id]).filter(Boolean);

          return (
            <div key={colId} className="flex flex-col  gap-4 h-[calc(100vh-400px)]">
              {/* Column Header */}

              {/* Droppable Area */}
              <Droppable droppableId={colId}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`flex-1 overflow-y-auto  min-h-0 p-3 rounded-md transition-colors duration-200 space-y-3 ${
                      snapshot.isDraggingOver
                        ? `bg-gray-200 border-2 ${getColumnBorderColor(colId)}`
                        : `bg-gray-200 border-2 border-transparent`
                    }`}
                  >
                    {bonds.map((bond, index) => (
                      <Draggable key={String(bond.id)} draggableId={String(bond.id)} index={index}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            <BondCard
                              bond={bond}
                              columnId={colId}
                              isDragging={snapshot.isDragging}
                              onClick={() => onCardClick?.(bond)}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          );
        })}
      </div>
    </DragDropContext>
  );
};

export default RenewalBoard;
